'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { SITE_REGION_CODES } from '@/lib/utils';
import { options } from '../ui/languageSwitcher';
import RegionSelectModal from '../layout/footer/regionSelectModal';

const REGION_CODES: string[] = [...SITE_REGION_CODES];
// Region-specific placeholder routes — not real content paths; don't carry
// these across when the user picks a different store in the geo popup.
const NON_TRANSFERABLE_PATHS = new Set(['coming-soon']);

function isAuHost(hostname: string) {
  return hostname === 'unimig.com.au' || hostname === 'www.unimig.com.au';
}

function setCookie(name: string, value: string, maxAge: number) {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; path=/; SameSite=Lax`;
}

export default function RegionPromptBanner() {
  const params = useParams<{ countryCode?: string }>();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [suggestedCode, setSuggestedCode] = useState<string | null>(null);

  const pathRegion = (params?.countryCode ?? '').toLowerCase();

  useEffect(() => {
    // Dismissed state lives in sessionStorage — clears when the tab is closed,
    // so returning visitors (new session) will see the popup fresh.
    if (sessionStorage.getItem('geo-popup-dismissed')) return;

    function getCookie(name: string): string | null {
      const match = document.cookie.match(
        new RegExp('(^|;\\s*)(' + name + ')=([^;]*)')
      );
      return match ? decodeURIComponent(match[3]) : null;
    }

    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="x-geo-popup"]'
    );
    const prompt = getCookie('geo_prompt') || meta?.content;

    if (!prompt) return;

    if (options.some((item) => item.code === prompt)) {
      setSuggestedCode(prompt);
    }
    // 'choose' = unsupported/unknown geo — open generic region picker
    setOpen(true);
  }, []);

  const suggestedRegion = useMemo(() => {
    return options.find((item) => item.code === suggestedCode) || null;
  }, [suggestedCode]);

  function dismiss() {
    sessionStorage.setItem('geo-popup-dismissed', '1');
    setCookie('geo_prompt', '', -1);
    if (REGION_CODES.includes(pathRegion)) {
      setCookie('geo_path_ack', pathRegion, 60 * 60 * 24 * 365);
    }
    setOpen(false);
  }

  function handleSelect(code: string) {
    sessionStorage.setItem('geo-popup-dismissed', '1');
    setCookie('geo_prompt', '', -1);
    setCookie('country_code', code, 60 * 60 * 24 * 365);
    if (REGION_CODES.includes(code)) {
      setCookie('geo_path_ack', code, 60 * 60 * 24 * 365);
    }

    const selected = options.find((item) => item.code === code);
    if (!selected) return;

    // Preserve the page the visitor was actually trying to reach (e.g. a product
    // deep link from Google/Facebook) instead of always bouncing them to the
    // homepage. `pathname` is the visible browser path, so strip any existing
    // region prefix (present on the multi-region unimig.com domain) before
    // rebuilding the URL for the newly selected region/domain.
    const segments = (pathname ?? '').split('/').filter(Boolean);
    const withoutRegion = REGION_CODES.includes(segments[0]?.toLowerCase())
      ? segments.slice(1)
      : segments;
    const subPath = withoutRegion
      .filter((seg) => !NON_TRANSFERABLE_PATHS.has(seg.toLowerCase()))
      .join('/');

    const targetDomain =
      selected.code === 'au'
        ? 'https://unimig.com.au'
        : `https://unimig.com/${selected.code}`;

    // Cookies cannot cross unimig.com ↔ unimig.com.au (different registrable
    // domains). Pass a one-shot ack param so the destination host can persist
    // the user's choice and skip showing the popup again.
    const hostname = window.location.hostname.toLowerCase();
    const needsRegionAck =
      (selected.code === 'au' && !isAuHost(hostname)) ||
      (selected.code !== 'au' && isAuHost(hostname));

    const target = new URL(
      subPath ? `${targetDomain}/${subPath}/` : `${targetDomain}/`
    );
    if (needsRegionAck) {
      target.searchParams.set('region_ack', selected.code);
    }

    window.location.href = target.toString();
  }

  if (!open) return null;

  return (
    <RegionSelectModal
      elevated
      title={
        suggestedRegion ? 'Select The Correct Store' : 'Choose Your Region'
      }
      description={
        suggestedRegion
          ? 'We detected that your location may not match this region. Choose your preferred store below.'
          : 'Select your region to view local pricing, currency and delivery options.'
      }
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          dismiss();
          return;
        }
        setOpen(true);
      }}
      options={options}
      onSelect={handleSelect}
    />
  );
}
